import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Alert, AlertProps, AlertType } from 'remodeling-web-mobile-sdk-ui';
import { useWebApp } from '../WebAppProvider';
import TrackingService from '@/services/tracking';

interface AlertOption<T extends AlertType>
  extends Omit<
    AlertProps<T>,
    'isOpen' | 'contentsStyledProps' | 'handleClose' | 'variant'
  > {
  type?: T;
  shouldIgnoreNativeBack?: boolean;
  gtmConfirmTagName?: string;
}
interface AlertContextType {
  isOpen: boolean;
  handleOpen: <T extends AlertType>(options: AlertOption<T>) => void;
  handleClose: () => void;
}

const AlertContext = createContext<AlertContextType>({
  isOpen: false,
  handleOpen: () => {},
  handleClose: () => {},
});

export function useAlert() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within a AlertContext');
  }
  return context;
}

/**
 * 공통 얼럿 provider
 * @author 이현우
 * @param param0
 * @returns
 */
function AlertProvider({ children }: { children?: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [alertOptions, setAlertOptions] =
    useState<AlertOption<AlertType> | null>();
  const { handleFireNativeBack, detachCallback } = useWebApp();

  const handleClose = useCallback(() => {
    setAlertOptions(null);
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(
    (alertOptions: AlertOption<AlertType>) => {
      setAlertOptions({
        ...alertOptions,
      });
      setIsOpen(true);

      handleFireNativeBack('FireNativeBack-AlertProvider', async () => {
        if (!alertOptions?.shouldIgnoreNativeBack) {
          alertOptions?.handleConfirm?.();
          handleClose();
        }
      });
    },
    [handleClose, handleFireNativeBack],
  );

  useEffect(() => {
    if (!isOpen) {
      detachCallback('FireNativeBack-AlertProvider');
    }
  }, [detachCallback, isOpen]);

  return (
    <AlertContext.Provider
      value={{
        isOpen,
        handleOpen,
        handleClose,
      }}
    >
      {children}
      <AlertContext.Consumer>
        {(value) => (
          <Alert<AlertType>
            {...value}
            {...(alertOptions as any)}
            variant={alertOptions?.type}
            handleConfirm={() => {
              alertOptions?.handleConfirm?.();
              value.handleClose();
            }}
            confirmButtonProps={{
              ...TrackingService.tagGTM(
                alertOptions?.gtmConfirmTagName
                  ? alertOptions.gtmConfirmTagName
                  : 'confirm_button',
              ),
            }}
          />
        )}
      </AlertContext.Consumer>
    </AlertContext.Provider>
  );
}

export default AlertProvider;
